import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ClaimPnrDisplay } from "@upclaim/sdk/src/data-contracts";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

type RawPnrDisplayView = ClaimPnrDisplay["view"];
type RawPnrDisplaySegment = RawPnrDisplayView["itinerary"][number];
type RawPnrDisplayCheckIn = RawPnrDisplaySegment["checkIns"][number];

/**
 * joi-to-typescript does not carry `.allow(null)` on an object into the generated type, so the
 * nullable objects of the contract (`view`, `sentinel`, a check-in's `checkedBags`) are restated
 * here rather than bending the API contract to the generator's limits.
 */
export type PnrDisplayCheckIn = Omit<RawPnrDisplayCheckIn, "checkedBags"> & {
  checkedBags: RawPnrDisplayCheckIn["checkedBags"] | null;
};
export type PnrDisplaySegment = Omit<RawPnrDisplaySegment, "checkIns"> & { checkIns: PnrDisplayCheckIn[] };
export type PnrDisplayView = Omit<RawPnrDisplayView, "itinerary"> & { itinerary: PnrDisplaySegment[] };
export type PnrDisplayTicket = PnrDisplayView["tickets"][number];
export type PnrDisplayFare = PnrDisplayView["fares"][number];
export type PnrDisplayTimelineEvent = PnrDisplayView["timeline"][number];

export type ClaimPnrDisplayResponse = Omit<ClaimPnrDisplay, "view" | "sentinel"> & {
  view: PnrDisplayView | null;
  sentinel: string | null;
};

/** Archived Amadeus PNR image (PNR_Display) of a claim — see TECH-928. */
export function useClaimPnrDisplay(claimId: string) {
  return useQuery({
    queryKey: queryKeys.claims.pnrDisplay(claimId),
    queryFn: async (): Promise<ClaimPnrDisplayResponse> => adminAPI.getClaimPnrDisplay(claimId),
  });
}

/**
 * Asks Amadeus again for the PNR image — at most once per reservation per 24 h, the API answers 429
 * with the next possible time otherwise. The refreshed view replaces the cached one directly.
 */
export function useRefreshClaimPnrDisplay(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (): Promise<ClaimPnrDisplayResponse> =>
      notify.promise(adminAPI.refreshClaimPnrDisplay(claimId), {
        loading: "Récupération du PNR auprès d'Amadeus…",
        success: (data: ClaimPnrDisplayResponse) =>
          data.status === "AVAILABLE"
            ? "Image PNR récupérée"
            : {
                message: "Amadeus n'a renvoyé aucune image",
                description:
                  data.status === "PURGED"
                    ? "Le PNR a été purgé par Amadeus."
                    : "La réservation n'est pas un PNR Amadeus actif.",
              },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.claims.pnrDisplay(claimId), data);
    },
  });
}
